import './styles/app.css';
import './styles/preview.css';
import './styles/print.css';

import { attachEditor } from './editor';
import { renderMarkdown, runMermaid, extractLanguages, ensureLanguages } from './renderer';
import { savePersisted, loadPersisted } from './persistence';
import { attachDropzone } from './dropzone';
import { attachPdfButton } from './pdf';
import { downloadProPdf } from './proPdf';
import { SAMPLE_MARKDOWN } from './sample';
import { setupTheme } from './theme';
import { attachSearch, type SearchStatus } from './search';

function showToast(message: string): void {
  const el = document.getElementById('toast');
  if (!el) return;
  el.textContent = message;
  el.hidden = false;
  setTimeout(() => {
    el.hidden = true;
  }, 2500);
}

interface SearchUI {
  open(): void;
  close(): void;
  isOpen(): boolean;
}

function setupSearchUI(search: ReturnType<typeof attachSearch>): SearchUI {
  const bar = document.getElementById('search-bar') as HTMLElement | null;
  const input = document.getElementById('search-input') as HTMLInputElement | null;
  const count = document.getElementById('search-count') as HTMLElement | null;
  const nextBtn = document.getElementById('search-next');
  const prevBtn = document.getElementById('search-prev');
  const closeBtn = document.getElementById('search-close');
  if (!bar || !input || !count) {
    return { open() {}, close() {}, isOpen: () => false };
  }

  function renderStatus(s: SearchStatus): void {
    count!.textContent = `${s.current} / ${s.total}`;
    count!.dataset.empty = String(s.total === 0 && s.query.length > 0);
  }
  search.onStatusChange(renderStatus);
  renderStatus(search.getStatus());

  function open(): void {
    bar!.hidden = false;
    requestAnimationFrame(() => {
      input!.focus();
      input!.select();
    });
  }
  function close(): void {
    bar!.hidden = true;
    search.clear();
    renderStatus(search.getStatus());
  }
  function isOpen(): boolean {
    return !bar!.hidden;
  }

  input.addEventListener('input', () => {
    search.setQuery(input.value);
  });
  input.addEventListener('keydown', (ev) => {
    if (ev.key === 'Enter') {
      ev.preventDefault();
      if (ev.shiftKey) search.prev();
      else search.next();
    } else if (ev.key === 'Escape') {
      ev.preventDefault();
      close();
    }
  });
  nextBtn?.addEventListener('click', () => {
    search.next();
    input.focus();
  });
  prevBtn?.addEventListener('click', () => {
    search.prev();
    input.focus();
  });
  closeBtn?.addEventListener('click', close);

  return { open, close, isOpen };
}

function setupTabs(): void {
  const tabEdit = document.getElementById('tab-edit')!;
  const tabPreview = document.getElementById('tab-preview')!;
  function activate(which: 'edit' | 'preview'): void {
    document.body.dataset.activePane = which;
    tabEdit.setAttribute('aria-selected', String(which === 'edit'));
    tabPreview.setAttribute('aria-selected', String(which === 'preview'));
  }
  activate('edit');
  tabEdit.addEventListener('click', () => activate('edit'));
  tabPreview.addEventListener('click', () => activate('preview'));
}

async function main(): Promise<void> {
  const textarea = document.getElementById('editor') as HTMLTextAreaElement;
  const previewEl = document.getElementById('preview') as HTMLElement;
  const editorPane = document.querySelector<HTMLElement>('.pane-editor')!;
  const pdfBtn = document.getElementById('pdf-btn') as HTMLElement;
  const proPdfBtn = document.getElementById('pro-pdf-btn') as HTMLElement | null;
  const sampleBtn = document.getElementById('sample-btn') as HTMLElement | null;

  const editor = attachEditor(textarea, { debounceMs: 200 });

  // Restore last input. First-visit users get an empty textarea + visible sample button.
  const restored = loadPersisted();
  if (restored) {
    editor.setValue(restored);
  }

  const emptyHint = document.getElementById('empty-hint');
  function updateEmptyState(): void {
    const isEmpty = editor.getValue().length === 0;
    if (sampleBtn) sampleBtn.hidden = !isEmpty;
    if (emptyHint) emptyHint.hidden = !isEmpty;
  }

  const search = attachSearch(previewEl);
  const searchUI = setupSearchUI(search);

  async function rerender(md: string): Promise<void> {
    try {
      // hljs 언어 정의는 사용 시점 lazy load — render 전에 ensure.
      await ensureLanguages(extractLanguages(md));
      previewEl.innerHTML = renderMarkdown(md);
      await runMermaid(previewEl);
      // 검색 바가 열려 있고 쿼리가 있으면 새 DOM에 다시 highlight.
      if (search.getStatus().query) search.reapply();
    } catch (err) {
      const message = err instanceof Error ? err.message : '알 수 없는 오류';
      // Build the error node with textContent so attacker-controlled exception
      // messages can't inject HTML into the preview pane.
      previewEl.replaceChildren();
      const errEl = document.createElement('div');
      errEl.className = 'render-error';
      errEl.textContent = `렌더 실패: ${message}`;
      previewEl.appendChild(errEl);
    }
  }

  editor.onChange((value) => {
    savePersisted(value);
    updateEmptyState();
    void rerender(value);
  });

  // Initial render reflecting whatever editor.setValue produced (or empty).
  await rerender(editor.getValue());
  updateEmptyState();

  if (sampleBtn) {
    sampleBtn.addEventListener('click', () => {
      editor.setValue(SAMPLE_MARKDOWN);
    });
  }

  // empty-hint 안의 "샘플 보기" 버튼도 같은 동작 (편집창 ghost 버튼과 별개 위치).
  const emptySampleBtn = document.getElementById('empty-sample');
  if (emptySampleBtn) {
    emptySampleBtn.addEventListener('click', () => {
      editor.setValue(SAMPLE_MARKDOWN);
    });
  }

  // 원클릭 PDF (Pro) — 백엔드 /api/pdf로 POST해서 서버사이드 PDF를 받는다.
  // Cloudflare Access의 family-access 정책으로 /api/pdf*가 보호되므로
  // 가족 외 사용자는 시도 시 OTP 로그인 페이지로 안내된다.
  if (proPdfBtn) {
    proPdfBtn.addEventListener('click', () => {
      void downloadProPdf({ preview: previewEl, onProgress: showToast });
    });
  }

  attachDropzone(editorPane, {
    onText: (text) => editor.setValue(text),
    onError: (msg) => showToast(msg),
  });

  attachPdfButton(pdfBtn);
  setupTabs();

  // ⌘F / Ctrl+F — 프리뷰 안에서 텍스트 검색. 브라우저 native find 대신 우리 검색 바를
  // 띄움. 편집 탭이어도 자동으로 프리뷰로 전환.
  window.addEventListener('keydown', (ev) => {
    if (!(ev.metaKey || ev.ctrlKey)) return;
    if (ev.key.toLowerCase() === 'f') {
      ev.preventDefault();
      document.body.dataset.activePane = 'preview';
      document.getElementById('tab-edit')?.setAttribute('aria-selected', 'false');
      document.getElementById('tab-preview')?.setAttribute('aria-selected', 'true');
      searchUI.open();
    }
  });

  const themeToggle = document.getElementById('theme-toggle');
  if (themeToggle) {
    setupTheme(themeToggle, () => {
      // 다크/라이트 토글 시 mermaid 다이어그램을 새 테마로 재렌더.
      void rerender(editor.getValue());
    });
  }

  // idle 시점에 자주 쓰는 hljs 언어 5개 미리 로드 — 사용자가 코드블록 입력 시
  // 첫 highlight 지연 없이 즉시 적용된다. 안 쓰이면 추가 비용은 idle 대기 chunk 다운로드 뿐.
  type IdleWindow = Window & { requestIdleCallback?: (cb: () => void) => number };
  const w: IdleWindow = window;
  const prefetchPopular = (): void => {
    void ensureLanguages(['javascript', 'typescript', 'python', 'bash', 'json']);
  };
  if (typeof w.requestIdleCallback === 'function') {
    w.requestIdleCallback(prefetchPopular);
  } else {
    setTimeout(prefetchPopular, 2000);
  }

  // Service Worker 등록 — PWA 설치 + 오프라인 캐시. main 흐름이 다 끝난 뒤 등록해
  // 첫 페이지 로드에 영향 없게. 실패는 조용히 무시(개발 환경/Safari old 등).
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js').catch(() => {
        // 등록 실패해도 사이트는 정상 — 캐시만 못 쓸 뿐.
      });
    });
  }
}

void main();
